<template>
  <div class="station-cutting-sheet-scan-page">
    <StationAlertMessage ref="glassAlertMessage" />
      
    <div class="station-scanner-entry my-4">
      <h2 class="text-muted">Search by stock to find the glass sheet to use or enter panel barcode</h2>
      <FormInputText ref="glassLevelLookup" v-model="glassLevelSearch" @click="onGlassLevelSearchClicked" @enter="onGlassLevelEntered" placeholder="Search" prependSlot>
        <template v-slot:prepend>
          <i class="far fa-search"></i>
        </template>
      </FormInputText>
    </div>
    <div v-if="glassLevelSearch" class="station-list-container">
      <div class="station-list station-search-list">
        <Spinner v-if="glassLevelLoading" />
        <h2 class="text-muted text-center mt-4" v-if="!glassLevelLoading">{{glassLevelResults.length}} results found for '{{glassLevelSearch}}'</h2>

        <div class="row my-2" v-for="stockResult of glassLevelResults" :key="stockResult.id">
          <div class="col-9"><h2>{{stockResult.name}}</h2></div>
          <div class="col-3">
            <div class="row no-gutters">
              <div class="col-4 pr-1">
                <button class="btn btn-success station-popover-button btn-block" @click.prevent="addGlassOffcut(stockResult.id, stockResult.name)"><i class="fal fa-layer-plus" /><br />Add Offcut</button>
              </div>
              <div class="col-4 px-1">
                <button class="btn btn-success station-popover-button btn-block" @click.prevent="selectGlassStock(stockResult.id)"><i class="fal fa-scalpel-path" /><br />Cut Stock</button>
              </div>
              <div class="col-4 pl-1">
                <button class="btn btn-success station-popover-button btn-block" @click.prevent="logSheetBreakage(stockResult.id)"><i class="fal fa-exclamation-triangle" /><br />Log Breakage</button>
              </div>
            </div>
          </div>
        </div>

        <h2 class="text-center text-danger mt-4" v-if="glassLevelSearchError">{{glassLevelSearchError}}</h2>
      </div>
    </div>  
  </div>
</template>


<style lang="scss" scoped>
.station-cutting-sheet-scan-page {
  flex: 100%;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items:stretch;
  overflow-x: hidden;
}
</style>

<script>
import _ from 'lodash'
import moment from 'moment'

import formMixin from '@/mixins/form'
import Utils from '@/utils/utils'
import EventBus from '@/utils/eventBus'
import StationAlertMessage from '@/apps/glass-production-station/src/components/station/stationAlertMessage'

import GlassSheetCuttingDialog from '@/apps/glass-production-station/src/views/glassSheetCuttingDialog'
import GlassBlockCuttingDialog from '@/apps/glass-production-station/src/views/glassBlockCuttingDialog'
import GlassAddOffcutDialog from '@/apps/glass-production-station/src/views/glassAddOffcutDialog'

import { LinkTypes } from '@/store/types'

export default {
  name: 'CuttingSheetScanPage',
  mixins: [formMixin],
  components: {
    StationAlertMessage
  },
  props: {
    productionStage: Object,
    productionStation: Object
  },
  data() {
    return {
      // Glass Consume
      glassLevelSearch: '',
      glassLevelSearchError: '',
      glassLevelResults: [],
      glassLevelLoading: false,
    }
  },
  watch: {
    glassLevelSearch: function() {
      this.glassLevelLoading = true
      this.debouncedGlassLevelSearch()
    }
  },
  created() {
    this.debouncedGlassLevelSearch = _.debounce(this.requestGlassLevelSearchInfo, 250)
  },
  mounted() {
    EventBus.on(EventBus.EventTypes.panelLabelPrinted, this.focusBarcodeEntry)
  },
  beforeDestroy() {
    EventBus.off(EventBus.EventTypes.panelLabelPrinted, this.focusBarcodeEntry)
  },
  methods: {
    onGlassLevelSearchClicked() {
      this.$refs.glassLevelLookup.select()
    },
    focusBarcodeEntry() {
      this.$refs.glassLevelLookup.select()
    },
    // Page Selected - Called when this cutting sheet scan page is selected to be shown
    pageSelected() {
      this.$refs.glassAlertMessage.hide()

      this.clearGlassCutSearch()
    },
    clearGlassCutSearch() {
      this.glassLevelSearch = ''
      this.$nextTick(() => {
        this.$refs.glassLevelLookup.focus()
      })
    },
    

    requestGlassLevelSearchInfo() {
      this.glassLevelSearchError = ''

      if(this.$data.glassLevelSearch == '') {
        this.glassLevelLoading = false
        this.glassLevelResults = []
        return
      }
      let maxResults = 20
      
      // Search for stock
      this.$api.get(`/search?maxResults=${maxResults}&search=${encodeURIComponent(this.$data.glassLevelSearch)}`)
      .then(response => {
        // Only process stock search results
        this.glassLevelResults = []
        response.data.forEach(resultList => {
          this.glassLevelResults = _.concat(this.glassLevelResults, _.compact(resultList.results.map(result => {
            if(result.linkType != LinkTypes.stock)
              return null
            return {
              id: result.id,
              name: result.name
            }
          })))
        })
      })
      .catch(err => {
        this.glassLevelSearchError = 'Error loading stock search results'
      })
      .finally(() => {
        this.glassLevelLoading = false
      })
    },
    onGlassLevelEntered() {
      if(this.$data.glassLevelSearch == '') {
        this.glassLevelLoading = false
        this.glassLevelResults = []
        return
      }

      // On enter, look for the barcode
      this.$apiService.glassProductionStationService.runBarcodeLookup(this.productionStation.id, this.$data.glassLevelSearch)
      .then(response => {

        // Panel
        if(response.data.model == 'productionPanel') {
          // If we have a valid production panel barcode, jump straight to the dialog with the stockId in the panel
          this.selectGlassStock(response.data.data.stockId)
          this.glassLevelLoading = false
        }
        // GlassLevel - Offcut
        else if(response.data.model == 'glassLevel') {
          // Force glass cut straight away
          this.selectGlassStock(response.data.data.stockId, response.data.data.id)
          this.glassLevelLoading = false
        }
        // Glass Block
        else if(response.data.model == 'glassBlock') {
          if(response.data.data.isOpened) {
            this.clearGlassCutSearch()
            this.$refs.glassAlertMessage.displayAlert('error', 'Glass Block Already Opened', 'Cannot scan in a glass block already opened')
            return
          }

          // Display the open glass block / cut sheet dialog
          this.showOpenGlassBlock(response.data.data.id, response.data.data)
          this.glassLevelLoading = false

        } else {
          this.clearGlassCutSearch()
          this.$refs.glassAlertMessage.displayAlert('error', 'Unknown Item Scanned', 'When scanning, use a panel or glass block barcode')
        }
        
      })
      .catch(err => {       
        this.clearGlassCutSearch()
        this.$refs.glassAlertMessage.displayAlert('error', 'Unknown Item Scanned', 'When scanning, use a panel, offcut or glass block barcode')
      })
      .finally(() => {
        this.glassLevelLoading = false          
      }) 
    },
    selectGlassStock(stockId, glassLevelId = null) {
      this.$refs.glassAlertMessage.hide()

      // Display stock sheet cutting dialog
      this.displayModal(GlassSheetCuttingDialog, { 
        stockId: stockId,
        glassLevelId: glassLevelId,
        productionStageId: this.productionStage.id,
        productionStationId: this.productionStation.id
      })
      .then((success) => {
        if(success) {
          this.clearGlassCutSearch()
          this.$refs.glassAlertMessage.displayAlert('success', 'Glass Sheet Cut', 'Glass sheet successfully flagged as cut')
        } else {
          this.$refs.glassLevelLookup.select()
          this.$refs.glassLevelLookup.focus()
        }
      })
    },
    addGlassOffcut(stockId, stockName) {
      this.$refs.glassAlertMessage.hide()

      // Display add offcut dialog
      this.displayModal(GlassAddOffcutDialog, { 
        stockId: stockId,
        stockName: stockName,
        productionStationId: this.productionStation.id
      })
      .then((success) => {
        if(success) {
          this.clearGlassCutSearch()
          this.$refs.glassAlertMessage.displayAlert('success', 'Offcut Added', 'Offcut added to inventory')
        } else {
          this.$refs.glassLevelLookup.select()
          this.$refs.glassLevelLookup.focus()
        }
      })
    },
    logSheetBreakage(stockId) {
      this.$refs.glassAlertMessage.hide()

      this.displayModal(GlassSheetCuttingDialog, { 
        stockId: stockId,
        productionStationId: this.productionStation.id,
        isBreakage: true
      })
      .then((success) => {
        if(success) {
          this.clearGlassCutSearch()
          this.$refs.glassAlertMessage.displayAlert('success', 'Sheet Breakage', 'Glass sheet successfully flagged as broken')
        } else {
          this.$refs.glassLevelLookup.select()
          this.$refs.glassLevelLookup.focus()
        }
      })
    },
    showOpenGlassBlock(glassBlockId, glassBlockData) {
      this.$refs.glassAlertMessage.hide()

      // Display glass block / sheet cutting dialog
      this.displayModal(GlassBlockCuttingDialog, { 
        glassBlockId: glassBlockId,
        glassBlock: glassBlockData,
        productionStageId: this.productionStage.id,
        productionStationId: this.productionStation.id
      })
      .then((success) => {
        if(success) {
          this.clearGlassCutSearch()
          this.$refs.glassAlertMessage.displayAlert('success', 'Glass Block Processed', 'Glass block opened and glass sheet(s) cut')
        } else {
          this.$refs.glassLevelLookup.select()
          this.$refs.glassLevelLookup.focus()
        }
      })
    },
  }
}
</script>